import { useState, useEffect, useRef } from 'react';
import { fetchNewsArticles, fetchRSSArticles, fetchYouTubeFeed } from '../api/feed';
import { fetchSourceMarkets, fetchAuthorMarkets } from '../api/markets';

const CACHE_KEY = 'paydirt_feed_cache';
const FETCH_TIMEOUT = 20000;

const loadCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const saveCache = (data) => {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch {}
};

const withTimeout = (promise) =>
  Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), FETCH_TIMEOUT)),
  ]);

const dedup = (arr) => {
  const seen = new Set();
  return arr.filter(a => {
    const key = (a.title || '').toLowerCase().trim();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const articleKey = (a) => a.url || a.id || (a.title || '').toLowerCase().trim();

const timeVal = s => {
  if (!s) return 999;
  const m = s.match(/(\d+)\s*(min|hour|day|week|month)/i);
  if (!m) return 999;
  const n = parseInt(m[1]);
  const u = m[2].toLowerCase();
  if (u.startsWith('min')) return n;
  if (u.startsWith('hour')) return n * 60;
  if (u.startsWith('day')) return n * 1440;
  if (u.startsWith('week')) return n * 10080;
  if (u.startsWith('month')) return n * 43200;
  return 999;
};

export function useNewsData() {
  const [newsArticles, setNewsArticles] = useState([]);
  const [sourceMarkets, setSourceMarkets] = useState([]);
  const [authorMarkets, setAuthorMarkets] = useState([]);
  const [videoReels, setVideoReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const hadCache = useRef(false);

  async function fetchAll() {
    try {
      const isBackground = hadCache.current;
      if (!isBackground) setLoading(true);

      const fast = Promise.all([
        withTimeout(fetchNewsArticles()).catch(() => null),
        withTimeout(fetchSourceMarkets()).catch(() => null),
        withTimeout(fetchAuthorMarkets()).catch(() => null),
      ]);

      const slow = Promise.all([
        withTimeout(fetchRSSArticles()).catch(() => null),
        withTimeout(fetchYouTubeFeed()).catch(() => null),
      ]);

      const [newsRes, srcRes, authRes] = await fast;
      let articles = [];
      if (newsRes?.ok) {
        const data = await newsRes.json();
        articles.push(...(data.articles || data || []));
      }
      setNewsArticles(prev => {
        const existing = new Set(prev.map(articleKey));
        const fresh = dedup(articles).filter(a => !existing.has(articleKey(a)));
        return fresh.length ? [...fresh, ...prev] : prev;
      });
      const srcMarkets = srcRes?.ok ? (await srcRes.json()).markets || [] : [];
      const authMarkets = authRes?.ok ? (await authRes.json()).markets || [] : [];
      if (srcMarkets.length) setSourceMarkets(srcMarkets);
      if (authMarkets.length) setAuthorMarkets(authMarkets);
      if (!isBackground) { setLoading(false); hadCache.current = true; }

      const [rssRes, vidRes] = await slow;
      let rssArticles = [];
      if (rssRes?.ok) {
        const data = await rssRes.json();
        rssArticles = data.articles || data || [];
        setNewsArticles(prev => {
          const existing = new Set(prev.map(articleKey));
          const fresh = dedup(rssArticles).filter(a => !existing.has(articleKey(a)));
          return fresh.length ? [...fresh, ...prev] : prev;
        });
      }
      const videos = vidRes?.ok ? (await vidRes.json()).videos || [] : [];
      if (videos.length) setVideoReels(videos);

      setNewsArticles(current => {
        saveCache({
          articles: current,
          videos: videos.length ? videos : undefined,
        });
        return current;
      });
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      if (!hadCache.current) { setLoading(false); hadCache.current = true; }
    }
  }

  useEffect(() => {
    const cached = loadCache();
    if (cached) {
      if (cached.articles?.length) setNewsArticles(cached.articles);
      // Markets are NOT restored from cache — they have live state
      // (open/closed) that must always come fresh from the API
      if (cached.videos?.length) setVideoReels(cached.videos);
      setLoading(false);
      hadCache.current = true;
    }
    fetchAll();
    const interval = setInterval(fetchAll, 60000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchAll();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const byCat = c => {
    const all = newsArticles.filter(a => (a.category || '').toLowerCase() === c.toLowerCase())
      .sort((a, b) => timeVal(a.publishedAt) - timeVal(b.publishedAt));
    if (!all.length) return [];

    const srcKey = a => (a.outlet || a.source || 'unknown').toLowerCase();
    const result = [];
    const srcCount = {};
    const used = new Set();

    result.push(all[0]);
    used.add(0);
    srcCount[srcKey(all[0])] = 1;

    const unseenSources = [...new Set(all.map(srcKey))].filter(s => !srcCount[s])
      .sort((a, b) => (a.includes('bbc') ? 1 : 0) - (b.includes('bbc') ? 1 : 0));
    for (const src of unseenSources) {
      const idx = all.findIndex((a, i) => !used.has(i) && srcKey(a) === src);
      if (idx >= 0) {
        result.push(all[idx]);
        used.add(idx);
        srcCount[src] = (srcCount[src] || 0) + 1;
      }
    }

    const remaining = all.filter((a, i) => !used.has(i))
      .sort((a, b) => {
        const ta = timeVal(a.publishedAt), tb = timeVal(b.publishedAt);
        if (ta !== tb) return ta - tb;
        return (srcKey(a).includes('bbc') ? 1 : 0) - (srcKey(b).includes('bbc') ? 1 : 0);
      });
    for (const a of remaining) {
      const src = srcKey(a);
      if ((srcCount[src] || 0) < 5) {
        result.push(a);
        srcCount[src] = (srcCount[src] || 0) + 1;
      }
    }
    return result;
  };

  return { newsArticles, sourceMarkets, authorMarkets, videoReels, loading, byCat, fetchAll };
}
